import { observable, action, makeAutoObservable } from "mobx";
import axios, { AxiosRequestConfig, AxiosResponse, AxiosError } from "axios";
import { RootStore } from "../RootStore";
import { ResponseMiddleware } from "./ResponseMiddleware";
import qs from "qs";
import { CLIENT_VERSION_HEADER_KEY, VOLTAGE_CLIENT_VERSION } from "../../data/config";
import { AppError } from "./errors/AppError";
import { AppErrors } from "./constants/AppErrors";

enum RequestStatus {
    NotRequested,
    InFlight,
    Successful,
    Error,
    Canceled
}

class RequestQueueError extends Error {
    // New error class
}

export interface IRequestConfig extends AxiosRequestConfig {
    type?: string;
    forceRefresh?: boolean;
    noHeaders?: boolean;
}

class Request {
    @observable id: string | number = "";
    @observable status = RequestStatus.NotRequested;
    @observable controller = new AbortController();
    @observable config: IRequestConfig;

    constructor(id: string | number, config: IRequestConfig) {
        makeAutoObservable(this);

        this.id = id;
        this.status = RequestStatus.InFlight;
        this.config = config;
        this.config.signal = this.controller.signal;
        if (!this.config.noHeaders) {
            this.config.headers = { 'Cache-Control': 'no-cache', [CLIENT_VERSION_HEADER_KEY]: VOLTAGE_CLIENT_VERSION };
        }
    }
}

export class RequestQueue {
    @observable queue = [] as Request[];

    private rootStore: RootStore;
    private responseMiddleware: ResponseMiddleware;
    private requestCounter = 0;

    constructor(rootStore: RootStore) {
        makeAutoObservable(this);

        this.rootStore = rootStore;
        this.responseMiddleware = new ResponseMiddleware(rootStore);
    }

    makeExternalRequest<T>(requestConfig: IRequestConfig) {
        return this.makeRequest<T>(requestConfig);
    }

    makeAPIRequest<T>(requestConfig: IRequestConfig) {
        return this.makeRequest<T>(requestConfig, this.apiRequestMiddleware);
    }

    private apiRequestMiddleware = (response: AxiosResponse<any, any>) => {
        this.responseMiddleware.checkForClientVersionUpdate(response.headers);
        this.responseMiddleware.checkForAPIError(response.data);
        this.responseMiddleware.parseCategoryLists(response.data);
        this.responseMiddleware.applyMiddleware(response.data);
    }

    private makeRequest<T>(requestConfig: IRequestConfig, middleware?: (response: AxiosResponse<any, any>) => void) {
        return new Promise<T /*| { error: APIError }*/>(async (resolve, reject) => {
            try {
                const requestId = this.generateRequestId(requestConfig);
                requestConfig = this.addParamSerializerIfNeeded(requestConfig);
                this.verifyRequestNotQueued(requestId);
                if (process.env.NODE_ENV === 'development') {
                    console.log('Request:');
                    console.log(requestConfig);
                }
                const response = await this.addRequest(requestId, requestConfig);

                if (process.env.NODE_ENV === 'development') {
                    console.log(`Response for ${requestId}:`);
                    console.log(response.data);
                }

                const finish = () => {
                    if (middleware) {
                        middleware(response);
                    }
                    resolve(response.data);
                }

                let delayForTesting = false;
                // delayForTesting = true;
                if (delayForTesting && process.env.NODE_ENV === 'development') {
                    setTimeout(() => {
                        finish();
                    }, 3000);
                } else {
                    finish();
                }
            } catch (error) {
                if (error instanceof RequestQueueError) {
                    console.log(error);
                } else if (error instanceof AxiosError) {
                    if (error.code === 'ERR_CANCELED') {
                        console.log(error);
                        return;
                    }

                    if (error.response === undefined) {
                        reject(new AppError(AppErrors.INTERNAL_SERVER_ERROR));
                        return;
                    }
                    if (error.response.status === 401) {
                        // Handle the user's session expiring
                        this.rootStore.userStore.setAuthenticatedUser();
                        await this.rootStore.userStore.checkForAuthenticatedUser();
                        if (!this.rootStore.userStore.isAuthenticated) {
                            this.rootStore.navigationStore.appwideDialogStates.sessionExpiredDialog.setOpen(true);
                        }

                        reject(new AppError(error.response.data))
                    } else if (error.response.data.metadata) {
                        const { recovery } = error.response.data.metadata;
                        if (recovery && 'activeProfileId' in recovery) {
                            if (this.rootStore.userStore.user.activeProfile?.id !== recovery.activeProfileId) {
                                await this.rootStore.userStore.checkForAuthenticatedUser();
                            }
                        }
                        reject(new AppError(error.response.data))
                    } else {
                        // TODO: Test what happens if response.data is an unexpected object
                        reject(new AppError(error.response.data))
                    }
                } else {
                    console.error(error);
                    reject(new AppError(AppErrors.UNEXPECTED_CLIENT_ERROR));
                }
            }
        });
    }

    /************* Add a new request to the queue *************/

    @action private addRequest(id: string | number, config: IRequestConfig) {
        return new Promise<AxiosResponse>(async (resolve, reject) => {
            const request = new Request(id, config);
            this.queue.push(request);
            await axios(request.config).then(response => {
                request.status = RequestStatus.Successful;
                resolve(response);
            }).catch((reason: AxiosError) => {
                request.status = RequestStatus.Error;
                reject(reason);
            });
        })
    }

    verifyRequestNotQueued(id: string | number) {
        if (this.queue.some(request => request.id === id)) {
            throw new RequestQueueError(`Request ${id} has already been queued.`);
        }
    }

    /************* Empty the queue, canceling any in flight requests *************/

    @action emptyQueue(type?: string) {
        if (type === undefined) {
            this.cancelQueuedRequests('All in flight requests canceled.');
            this.queue = [] as Request[];
        } else {
            this.cancelQueuedRequestsWithType(type);
        }
    }

    @action private cancelQueuedRequestsWithType(type: string) {
        this.queue.filter(request => request.config.type === type).forEach(request => {
            this.cancelQueuedRequest(request, `Cancelling all requests of type '${type}'.`)
        });
    }

    private cancelQueuedRequests(message: string) {
        this.queue.forEach(request => this.cancelQueuedRequest(request, message));
    }

    private cancelQueuedRequest(request: Request, message: string) {
        if (request.status !== RequestStatus.InFlight) return;
        request.controller.abort(message + " " + request.id);
        request.status = RequestStatus.Canceled;
    }

    /************* Private helper methods *************/

    @action private generateRequestId(config: IRequestConfig) {
        // Generates a unique id for each put, post, delete request.
        // Must be unique since the request queue ignores requests with
        // ids matching an existing request.
        const uniqueIdNeeded = config.method?.toLowerCase() !== 'get';
        const id = `${config.url}${config.data ? JSON.stringify(config.data) : ''}`;
        if (uniqueIdNeeded || config.forceRefresh) {
            this.requestCounter += 1;
            return `${id}-${Date.now()}-${this.requestCounter}}`;
        } else {
            return `${id}`;
        }
    }

    private addParamSerializerIfNeeded(config: IRequestConfig): IRequestConfig {
        if (config.method?.toLowerCase() === 'get' && config.params !== undefined) {
            return {
                ...config,
                paramsSerializer: function (params) {
                    const stringifiedParams = qs.stringify(params);
                    return stringifiedParams;
                }
            }
        }
        return config;
    }
}